import React from "react";
import Icon from "components/AppIcon";

const fmt = (val) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 })?.format(val);

const fmtKg = (val) =>
  new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })?.format(val);

export default function PriceBreakdown({ result }) {
  if (!result) {
    return (
      <div className="card p-4 md:p-6 flex flex-col items-center justify-center min-h-[220px] gap-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "var(--color-muted)" }}>
          <Icon name="Calculator" size={32} color="var(--color-muted-foreground)" strokeWidth={1.5} />
        </div>
        <div className="text-center">
          <p className="font-heading font-semibold text-base" style={{ color: "var(--color-text-primary)" }}>
            Sin cálculo activo
          </p>
          <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Configure los parámetros y pulse &quot;Calcular Precio&quot;
          </p>
        </div>
      </div>
    );
  }

  const rows = [
    { label: "Valor Benchmark (€/t)", value: fmt(result?.benchmarkValue), icon: "TrendingUp", color: "var(--color-text-primary)", positive: true },
    { label: `Ajuste Yield (${result?.yieldPct}%)`, value: `- ${fmt(result?.yieldDeduction)}`, icon: "Percent", color: "var(--color-warning)", positive: false },
    { label: "Coste Logística", value: `- ${fmt(result?.costLogistics)}`, icon: "Truck", color: "var(--color-warning)", positive: false },
    { label: "Coste Procesamiento", value: `- ${fmt(result?.costProcessing)}`, icon: "Settings", color: "var(--color-warning)", positive: false },
    { label: `Margen Objetivo (${result?.targetMargin}%)`, value: `- ${fmt(result?.marginDeduction)}`, icon: "Target", color: "var(--color-warning)", positive: false },
    {
      label: result?.riskBufferMode === 'manual'
        ? `Buffer de Riesgo Manual (${result?.riskBufferPct?.toFixed(1)}%)`
        : `Buffer de Riesgo VaR 95% (${result?.riskBufferPct?.toFixed(2)}%)`,
      value: `- ${fmt(result?.riskBufferDeduction)}`,
      icon: "Shield",
      color: "var(--color-error)",
      positive: false,
    },
  ];

  const riskLevel = result?.riskLevel;
  const riskConfig = {
    bajo: { color: "var(--color-success)", bg: "bg-emerald-50", label: "Riesgo Bajo", icon: "CheckCircle" },
    medio: { color: "var(--color-warning)", bg: "bg-amber-50", label: "Riesgo Medio", icon: "AlertTriangle" },
    alto: { color: "var(--color-error)", bg: "bg-red-50", label: "Riesgo Alto", icon: "AlertCircle" },
  };
  const risk = riskConfig?.[riskLevel] || riskConfig?.medio;

  return (
    <div className="card p-4 md:p-6 flex flex-col gap-5" data-tour="calc-result">
      {/* Max Purchase Price */}
      <div className="rounded-xl p-4 md:p-5 text-center" style={{ background: "linear-gradient(135deg, var(--color-primary) 0%, #334155 100%)" }}>
        <p className="text-xs font-medium uppercase tracking-widest mb-1" style={{ color: "rgba(248,250,252,0.7)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
          Precio Máximo de Compra
        </p>
        <p className="font-heading font-bold text-3xl md:text-4xl" style={{ color: "#F8FAFC" }}>
          {fmt(result?.maxPurchasePrice)}
        </p>
        <p className="text-sm mt-1" style={{ color: "rgba(248,250,252,0.6)", fontFamily: "'JetBrains Mono', monospace" }}>
          por tonelada métrica
        </p>
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${risk?.bg}`} style={{ color: risk?.color }}>
            <Icon name={risk?.icon} size={13} color={risk?.color} strokeWidth={2.5} />
            {risk?.label}
          </span>
        </div>
      </div>
      {/* Total for batch */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg p-3 text-center" style={{ background: "var(--color-muted)" }}>
          <p className="text-xs mb-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Peso Neto</p>
          <p className="font-data font-semibold text-base" style={{ color: "var(--color-text-primary)" }}>{fmtKg(result?.netWeight)} kg</p>
        </div>
        <div className="rounded-lg p-3 text-center" style={{ background: "var(--color-muted)" }}>
          <p className="text-xs mb-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Total Lote</p>
          <p className="font-data font-semibold text-base" style={{ color: "var(--color-success)" }}>{fmt(result?.totalBatchPrice)}</p>
        </div>
      </div>
      {/* Breakdown rows */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
          Desglose del Cálculo
        </p>
        <div className="flex flex-col gap-2">
          {rows?.map((row, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <div className="flex items-center gap-2">
                <Icon name={row?.icon} size={14} color={row?.color} strokeWidth={2} />
                <span className="text-sm" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>{row?.label}</span>
              </div>
              <span className="font-data text-sm font-medium" style={{ color: row?.positive ? "var(--color-text-primary)" : row?.color }}>
                {row?.value}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between py-2 mt-1 rounded-lg px-3" style={{ background: "var(--color-muted)" }}>
            <span className="text-sm font-semibold" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Precio Máximo Compra
            </span>
            <span className="font-data font-bold text-base" style={{ color: "var(--color-accent)" }}>
              {fmt(result?.maxPurchasePrice)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}